import { apiSlice } from "../store/app.api";
import { supabase } from "../utils/API";

type PaginatedMovieParams = {
  year?: number;
  title?: string;
  page?: number;
  rowsPerPage?: number;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
};

const getPaginationRange = (page = 1, rowsPerPage = 100) => {
  const safePage = Math.max(Number(page || 1), 1);
  const safeRowsPerPage = Math.max(Number(rowsPerPage || 100), 1);

  const from = (safePage - 1) * safeRowsPerPage;
  const to = from + safeRowsPerPage - 1;

  return { from, to };
};

const moviesApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getMovies: build.query<any, PaginatedMovieParams>({
      queryFn: async ({
        year = new Date().getFullYear(),
        title = "",
        page = 1,
        rowsPerPage = 100,
        sortColumn = "date_approved_last",
        sortDirection = "desc",
      }) => {
        const { from, to } = getPaginationRange(page, rowsPerPage);
        const keyword = title.trim();

        let query = supabase
          .from("movies")
          .select("*", { count: "exact" })
          .gte("date_approved_last", `${year}-01-01`)
          .lt("date_approved_last", `${year + 1}-01-01`)
          .order(sortColumn, { ascending: sortDirection === "asc" })
          .range(from, to);

        if (keyword) {
          query = query.ilike("title", `%${keyword}%`);
        }

        const { data, count, error } = await query;

        if (error) {
          return { error } as any;
        }

        return {
          data: {
            list: data ?? [],
            count: count ?? 0,
          },
        };
      },
      providesTags: ["movies"],
    }),

    getMoviePosters: build.query<any, PaginatedMovieParams>({
      queryFn: async ({
        year = new Date().getFullYear(),
        title = "",
        page = 1,
        rowsPerPage = 100,
      }) => {
        const { from, to } = getPaginationRange(page, rowsPerPage);
        const keyword = title.trim();

        let query = supabase
          .from("movie_posters")
          .select("*", { count: "exact" })
          .gte("date_approved_last", `${year}-01-01`)
          .lt("date_approved_last", `${year + 1}-01-01`)
          .order("date_approved_last", { ascending: false })
          .range(from, to);

        if (keyword) {
          query = query.ilike("title", `%${keyword}%`);
        }

        const { data, count, error } = await query;

        if (error) {
          return { error } as any;
        }

        return {
          data: {
            list: data ?? [],
            count: count ?? 0,
          },
        };
      },
      providesTags: ["movies"],
    }),

    getMovieSchedule: build.query<any, any>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("movie_schedule")
          .select("*");

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      providesTags: ["movies"],
    }),

    getCategoryById: build.query<any, any>({
      queryFn: async (id: string) => {
        const { data, error } = await supabase
          .from("categories")
          .select("name")
          .eq("id", id)
          .limit(1);

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      providesTags: ["movies"],
    }),

    getCategories: build.query<any, any>({
      queryFn: async () => {
        const { data, error } = await supabase.from("categories").select("*");

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      providesTags: ["movies"],
    }),

    getGenres: build.query<any, any>({
      queryFn: async () => {
        const { data, error } = await supabase.from("filters").select("*");

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      providesTags: ["movies"],
    }),

    getMovieReviewers: build.query<any, any>({
      queryFn: async (ids: any) => {
        const { data, error } = await supabase
          .from("board_member")
          .select("*")
          .in("id", ids);

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      providesTags: ["movies"],
    }),

    deleteMovie: build.mutation<any, any>({
      queryFn: async (id) => {
        const { data, error } = await supabase
          .from("movies")
          .delete()
          .eq("id", id);

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      invalidatesTags: ["movies"],
    }),

    deleteMovieSchedule: build.mutation<any, any>({
      queryFn: async (id) => {
        const { data, error } = await supabase
          .from("movie_schedule")
          .delete()
          .eq("id", id);

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      invalidatesTags: ["movies"],
    }),

    addMove: build.mutation<any, any>({
      queryFn: async (movie: any) => {
        const { data, error } = await supabase
          .from("movies")
          .insert(movie)
          .select()
          .single();

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      invalidatesTags: ["movies"],
    }),

    addMovieSchedule: build.mutation<any, any>({
      queryFn: async (movie: any) => {
        const { data, error } = await supabase
          .from("movie_schedule")
          .insert(movie)
          .select();

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      invalidatesTags: ["movies"],
    }),

    updateMovie: build.mutation<any, any>({
      queryFn: async (movie: any) => {
        const { id, ...payload } = movie;

        const { data, error } = await supabase
          .from("movies")
          .update(payload)
          .eq("id", id)
          .select();

        if (error) {
          return { error } as any;
        }

        return { data };
      },
      invalidatesTags: ["movies"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetMoviesQuery,
  useGetMoviePostersQuery,
  useDeleteMovieMutation,
  useAddMoveMutation,
  useAddMovieScheduleMutation,
  useUpdateMovieMutation,
  useGetCategoryByIdQuery,
  useGetCategoriesQuery,
  useGetMovieReviewersQuery,
  useGetMovieScheduleQuery,
  useDeleteMovieScheduleMutation,
  useGetGenresQuery,
} = moviesApi;